
import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, KeyRound, Loader2, ShieldCheck } from "lucide-react";

import { Layout } from "@/components/Layout";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const formSchema = z.object({
  password: z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(72, "Password must be no more than 72 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Verify the token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("No reset token provided");
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({
              token,
              action: "validate",
            }),
          }
        );

        const result = await response.json();

        if (!response.ok || !result.valid) {
          setError(result.error || "Invalid or expired reset token");
          setIsVerified(false);
        } else {
          setIsVerified(true);
        }
      } catch (error) {
        console.error("Error validating token:", error);
        setError("Failed to validate reset token");
        setIsVerified(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const onSubmit = async (values: FormValues) => {
    if (isSubmitting || !isVerified) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            token,
            password: values.password,
            action: "reset",
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to reset password");
      }

      setIsReset(true);
      toast.success("Password Reset Complete", {
        description: "Your password has been successfully reset."
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error("Reset Failed", {
        description: error instanceof Error ? error.message : "Failed to reset your password. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (isVerifying) {
      return (
        <div className="text-center p-6">
          <Loader2 className="mx-auto h-12 w-12 text-primary animate-spin mb-4" />
          <h3 className="text-lg font-medium mb-2">Verifying Your Link</h3>
          <p className="text-muted-foreground">
            Please wait while we verify your password reset link.
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-6 bg-destructive/10 rounded-lg">
          <ShieldCheck className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">Invalid Reset Link</h3>
          <p className="text-muted-foreground mb-6">
            {error}. Please request a new password reset link.
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/forgot-password')}
          >
            Request New Link
          </Button>
        </div>
      );
    }

    if (isReset) {
      return (
        <div className="text-center p-6 bg-muted/30 rounded-lg">
          <Check className="mx-auto h-12 w-12 text-primary mb-4" />
          <h3 className="text-lg font-medium mb-2">Password Reset Successful</h3>
          <p className="text-muted-foreground mb-6">
            Your password has been reset successfully. You will be redirected to the login page shortly.
          </p>
          <Button
            className="w-full"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </div>
      );
    }

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input type="password" placeholder="Create new password" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input type="password" placeholder="Confirm new password" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Processing..." : "Reset Password"}
          </Button>
        </form>
      </Form>
    );
  };

  return (
    <Layout>
      <section className="py-12 md:py-20 bg-gradient-to-b from-clay-50 to-background">
        <div className="container max-w-md mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-card border border-border rounded-xl p-8 shadow-sm"
          >
            <div className="text-center mb-8">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-clay-100 text-clay-800 rounded-full mb-4">
                Password Reset
              </span>
              <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">
                Create New Password
              </h1>
              <p className="text-muted-foreground">
                Enter a new password for your account.
              </p>
            </div>

            {renderContent()}

            {!isReset && !error && (
              <div className="mt-6">
                <div className="flex items-center gap-2 my-4">
                  <Separator className="flex-grow" />
                  <span className="text-xs text-muted-foreground">OR</span>
                  <Separator className="flex-grow" />
                </div>

                <p className="text-center text-sm text-muted-foreground">
                  Remember your password?{" "}
                  <Link to="/login" className="text-primary hover:underline">
                    Log in
                  </Link>
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default ResetPassword;
